import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Alert } from '@trussworks/react-uswds';
import { useFormContext } from 'react-hook-form';

import { useFormStore, useRequest } from '@stores';
import classNames from 'classnames';

import { QUESTION_TYPE } from '@components/data-driven-form/ddf.validation';
import { getEnvironmentVisibility } from '@components/environment-visibility/EnvironmentVisibility';
import { MarkdownRenderer } from '@components/markdown-renderer/MarkdownRenderer';

import { getEndContent, getFormContentVisibility, getOtherProps } from './helpers';
import { QuestionData, QuestionDataById, QuestionValue } from './types';

const showDebugInfo = getEnvironmentVisibility({ mode: 'show', local: true });

interface QuestionDocumentFile {
  isQuestionLocation?: boolean;
  documentQuestionIds?: number[];
  [key: string]: unknown;
}

interface InputComponentProps {
  defaultValue?: QuestionValue;
  fileDownload?: QuestionDocumentFile[];
  id?: string;
  label?: React.ReactNode;
  name?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  required?: boolean;
  showOptionalText?: boolean;
  tooltip?: string;
  [key: string]: unknown; // Allow additional props
}

interface QuestionProps {
  allowAutoScroll?: boolean;
  componentID?: string | number;
  defaultValue?: QuestionValue;
  formDocuments?: QuestionDocumentFile[];
  formId?: string | number;
  isConditionallyDisplayed?: boolean;
  inputMap: Record<number, React.ComponentType<InputComponentProps>>;
  inputNameOverride?: string;
  isReadOnly?: boolean;
  onChange?: ({
    inputName,
    isEndConditionMet,
    value,
  }: {
    inputName: string;
    isEndConditionMet: boolean;
    onChangeProps?: Record<string, unknown>;
    value: QuestionValue;
  }) => void;
  onChangeProps?: Record<string, unknown>;
  questionData: QuestionData;
  questionsById?: QuestionDataById;
}

const Question = ({
  allowAutoScroll,
  componentID,
  defaultValue,
  formDocuments,
  formId,
  isConditionallyDisplayed = true,
  inputMap,
  inputNameOverride,
  isReadOnly,
  onChange = () => {},
  onChangeProps = {},
  questionData,
  questionsById,
}: QuestionProps) => {
  const isEndConditionMet = useRef<boolean>(false);

  const {
    allowsMultiselect,
    clearQuestionIdsOnChange = [],
    endConditions = [],
    isRequired,
    lists,
    options,
    parentThreads = [],
    question,
    questionId,
    questionName,
    questionTypeId,
  } = questionData;

  const { selectedRequestId } = useRequest();
  const { setQuestionVisibility } = useFormStore();
  const { getValues, setValue } = useFormContext();

  const reqTypeId = Number(selectedRequestId);
  const inputName = inputNameOverride || questionName || '';
  const formValues = getValues();
  const inputValue = formValues?.[inputName];
  const isQuestionVisible = isConditionallyDisplayed ? getFormContentVisibility({ formValues, parentThreads }) : true;
  const Input = inputMap[allowsMultiselect ? QUESTION_TYPE.multiselect : questionTypeId];

  const endContent = useMemo(
    () => (inputValue !== undefined ? getEndContent(inputValue, endConditions, questionTypeId, reqTypeId) : null),
    [inputValue, endConditions, questionTypeId, reqTypeId]
  );
  isEndConditionMet.current = Boolean(endContent);

  const questionDocumentFiles = formDocuments?.filter(
    (file) => file?.isQuestionLocation && file?.documentQuestionIds?.includes(questionId)
  );

  const isContactInput = questionTypeId === QUESTION_TYPE.contact;
  const isDateInput = questionTypeId === QUESTION_TYPE.date;
  const isFileInput = questionTypeId === QUESTION_TYPE.file;
  const isLongAnswerInput = questionTypeId === QUESTION_TYPE.longAnswer;
  const isNumberInput = questionTypeId === QUESTION_TYPE.number;
  const isOptionInput = questionTypeId === QUESTION_TYPE.option;
  const isShortAnswerInput = questionTypeId === QUESTION_TYPE.shortAnswer;
  const isTableInput = questionTypeId === QUESTION_TYPE.table;
  const isYesNoInput = questionTypeId === QUESTION_TYPE.yesNo;
  const isInformation = questionTypeId === QUESTION_TYPE.statement;
  const alertType = questionData?.alertType || 'info';

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, isEndConditionMet: boolean) => {
      if (!isQuestionVisible) return;

      const eventIsValue = (isOptionInput && allowsMultiselect) || isTableInput || isYesNoInput || isContactInput;
      const value = eventIsValue ? e : e.target.value;

      clearQuestionIdsOnChange.forEach((id) => {
        const fieldName = questionsById?.[id]?.questionName as string;
        setValue(fieldName, undefined);
      });

      onChange({ inputName, isEndConditionMet, value: value as QuestionValue, onChangeProps });
    },
    [
      allowsMultiselect,
      clearQuestionIdsOnChange,
      inputName,
      isContactInput,
      isOptionInput,
      isQuestionVisible,
      isTableInput,
      isYesNoInput,
      onChange,
      onChangeProps,
      questionsById,
      setValue,
    ]
  );

  useEffect(() => {
    if (!isQuestionVisible) {
      setValue(inputName, undefined);
    }
  }, [isQuestionVisible, inputName, setValue]);

  useEffect(() => {
    if (!isConditionallyDisplayed) return;

    setQuestionVisibility(questionId, isQuestionVisible);

    if (isQuestionVisible && allowAutoScroll) {
      // Scroll to the question when it becomes visible
      const element = document.getElementById(inputName);
      if (element) {
        element.scrollIntoView();
      }
    }
  }, [allowAutoScroll, inputName, isConditionallyDisplayed, isQuestionVisible, questionId, setQuestionVisibility]);

  if (!isQuestionVisible) {
    return null;
  }

  const containerStyles = classNames('ddf-question', {
    'date-picker': isDateInput,
    'long-answer': isLongAnswerInput,
    'short-answer': isShortAnswerInput,
    'yes-no': isYesNoInput,
    file: isFileInput,
    number: isNumberInput,
    option: isOptionInput,
  });

  const otherProps = getOtherProps({
    allowsMultiselect,
    componentID,
    defaultValue,
    formId,
    handleOnChange,
    isContactInput,
    isDateInput,
    isFileInput,
    isLongAnswerInput,
    isNumberInput,
    isOptionInput,
    isReadOnly,
    isShortAnswerInput,
    isTableInput,
    lists,
    onChangeProps,
    options,
    questionData,
    questionDocumentFiles,
    questionId,
  });

  if (isInformation) {
    return (
      <Alert headingLevel='h4' type={alertType} slim noIcon className='margin-bottom-2'>
        <MarkdownRenderer text={question} />
      </Alert>
    );
  }

  const label = showDebugInfo ? (
    <>
      <b>q{questionId}</b>: <MarkdownRenderer displayInline text={question} />
    </>
  ) : (
    <MarkdownRenderer displayInline text={question} />
  );

  return (
    <div className={containerStyles}>
      <Input
        id={inputName}
        label={label}
        name={inputName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange(e, isEndConditionMet.current)}
        readOnly={isReadOnly}
        required={isRequired}
        showOptionalText={questionData?.showOptionalText}
        tooltip={questionData?.helpInfo}
        {...otherProps}
      />
      {endContent}
    </div>
  );
};

export default Question;
